<template>
  <ClientOnly>
    <Teleport to=".modal-target">
      <FadeInTransition>
        <FocusTrap
          :escape-deactivates="true"
          :return-focus-on-deactivate="true"
          :click-outside-deactivates="true"
          :initial-focus="getInitialFocusElement"
        >
          <div
            class="fixed inset-0 z-100 flex w-screen flex-col"
            :class="{
              'p-0': isCover,
              'p-0 sm:p-6': isAddToBasketPopover,
              'p-6': !isCover && !isAddToBasketPopover,
              'h-screen': isFullHeight,
              'justify-center': !isAddToBasketPopover,
              'justify-end sm:justify-center': isAddToBasketPopover,
            }"
          >
            <div class="absolute inset-0 bg-black-20" @click="$emit('close')" />
            <div
              class="relative mx-auto flex flex-col bg-white"
              :class="[
                {
                  'h-1/3 w-full sm:w-3/4 lg:w-1/3': isSmall,
                  'h-1/2 w-full sm:w-3/4 lg:w-1/2': isMedium,
                  'h-3/4 w-full sm:w-3/4 lg:w-3/4': isLarge,
                  'size-full': isFull || isCover,
                  rounded: !isCover,
                },
                customClass,
              ]"
              :style="isAddToBasketPopover ? { maxHeight: '75%' } : undefined"
            >
              <div ref="slotContainer" :class="innerClass">
                <slot></slot>
              </div>
              <div v-if="showCloseButton" class="absolute right-6 top-6 z-100">
                <Button
                    icon="close"
                    type="ghost"
                    :title="$t('dialog.close')"
                    :aria-label="$t('dialog.close')"
                    class="-m-4 rounded-full p-4 focus:ring-black"
                    @click="$emit('close')"
                />
              </div>
            </div>
          </div>
        </FocusTrap>
      </FadeInTransition>
    </Teleport>
  </ClientOnly>
</template>

<script setup lang="ts">
import { FocusTrap } from 'focus-trap-vue'

const { lastInteractionType } = useUiState()

const props = defineProps({
  innerClass: {
    type: String,
    default: undefined,
  },
  showCloseButton: {
    type: Boolean,
    default: true,
  },
  size: {
    type: String as PropType<'none' | 'sm' | 'md' | 'lg' | 'full' | 'cover'>,
    enum: ['none', 'sm', 'md', 'lg', 'full', 'cover'],
    default: 'md',
  },
  isFullHeight: {
    type: Boolean,
    default: true,
  },
  customClass: {
    type: String,
    default: undefined,
  },
  isAddToBasketPopover: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits(['close'])
const slotContainer = ref<HTMLElement | null>(null)

const handleKeyUpEvent = (event: KeyboardEvent) => {
  if (event.key === 'Escape') {
    emit('close')
  }
}

const getInitialFocusElement = () => {
  if (!slotContainer.value) return false

    // Only auto-focus if the user is navigating via keyboard
  if (lastInteractionType.value !== 'keyboard') {
    return false
  }
  
  return slotContainer.value.querySelector<HTMLElement>(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
  ) || false
}

onMounted(() => {
  document.addEventListener('keyup', handleKeyUpEvent)
})

onUnmounted(() => {
  document.removeEventListener('keyup', handleKeyUpEvent)
})

const isSmall = computed(() => props.size === 'sm')
const isMedium = computed(() => props.size === 'md')
const isLarge = computed(() => props.size === 'lg')
const isFull = computed(() => props.size === 'full')
const isCover = computed(() => props.size === 'cover')
</script>
